import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Clock3, Lock, CheckCircle2, AlertCircle } from 'lucide-react';
import Button from '../ui/Button';
import Drawer from '../ui/Drawer';
import ConfirmDialog from '../ui/ConfirmDialog';
import Switch from '../ui/Switch';
import { apiFetch } from '../../services/apiClient';
import PageHeader from '../ui/PageHeader';
import AddNewButton from '../ui/AddNewButton';
import ItemCard, { CARD_GRID, CardSection } from '../ui/ItemCard';

// Routed through apiFetch so this page follows the same base URL, credentials
// and error handling as the rest of the app.
const HHMM_REGEX = /^(?:[01]\d|2[0-3]):[0-5]\d$/;

const normalizeRow = (raw) => ({
  id: String(raw.id || ''),
  code: String(raw.short_code ?? raw.shortCode ?? raw.id ?? ''),
  label: String(raw.label || ''),
  start: String(raw.start_time ?? raw.startTime ?? ''),
  end: String(raw.end_time ?? raw.endTime ?? ''),
  breakMinutes: String(raw.break_minutes ?? raw.breakMinutes ?? 0),
  isOvernight: raw.is_overnight ?? raw.isOvernight,
  color: String(raw.color || ''),
  isActive: Boolean(raw.is_active ?? raw.isActive ?? true),
});

const isApiSuccess = (response, body) => response.ok && (body?.success === undefined || body?.success === true);

const parseJsonSafe = async (response) => {
  try {
    return await response.json();
  } catch {
    return null;
  }
};

// The internal-id slugifier core: lowercase, non-alphanumeric runs collapse
// to one underscore, leading/trailing underscores trimmed. Returns '' when
// nothing usable survives (symbols/whitespace only, or empty input) -- never
// a fallback -- so callers that care about "is this actually empty" (live
// auto-link, validation) can tell that apart from a real slug.
const slugify = (code) => code.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');

// Convenience wrapper for the few places that need SOME id no matter what
// (the taken-check and final submission, both only reached after validateForm
// has already confirmed the input slugifies to something real).
const normalizeIdFromCode = (code) => slugify(code) || 'shift';

const deriveOvernight = (start, end) => {
  if (!HHMM_REGEX.test(start) || !HHMM_REGEX.test(end)) return false;
  return end < start;
};

// Disambiguate a taken id the same way the old auto-generator did: id, id_2,
// id_3, ... first one not already in use.
const disambiguateId = (base, taken) => {
  if (!taken.has(base)) return base;
  let idx = 2;
  let candidate = `${base}_${idx}`;
  while (taken.has(candidate)) {
    idx += 1;
    candidate = `${base}_${idx}`;
  }
  return candidate;
};

const toApiPayload = (form) => ({
  label: form.label.trim(),
  short_code: form.code.trim(),
  start_time: form.start,
  end_time: form.end,
  break_minutes: Number(form.breakMinutes),
  is_overnight: deriveOvernight(form.start, form.end),
  color: form.color.trim() || '#D1D5DB',
  is_active: Boolean(form.isActive),
});

const validateForm = (form, mode, existingRows, originalRow) => {
  const code = form.code.trim();
  if (!code) return 'Code is required.';

  const duplicateCode = existingRows.some(
    (r) => r.id !== (originalRow?.id) && r.code.trim().toLowerCase() === code.toLowerCase()
  );
  if (duplicateCode) return 'Code must be unique.';

  if (!HHMM_REGEX.test(form.start)) return 'Start must be HH:MM.';
  if (!HHMM_REGEX.test(form.end)) return 'End must be HH:MM.';

  const breakValue = Number(form.breakMinutes);
  if (!Number.isInteger(breakValue) || breakValue < 0) return 'Break must be an integer >= 0.';

  if (mode === 'add') {
    if (!form.id.trim()) return 'Internal ID is required.';
    if (!slugify(form.id)) return 'Internal ID must contain at least one letter or number.';
    const slug = normalizeIdFromCode(form.id);
    if (existingRows.some((r) => r.id === slug)) return 'This Internal ID is already in use.';
  }

  return null;
};


const OvernightBadge = ({ value }) => (
  <span className={`px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-widest ${value ? 'bg-slate-700 text-white' : 'bg-slate-50 text-slate-400 border border-slate-200'}`}>
    {value ? 'Overnight' : 'Same day'}
  </span>
);

const StatusBadge = ({ active }) => (
  <span className={`px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-widest ${active ? 'bg-emerald-50 text-emerald-600 border border-emerald-200' : 'bg-slate-50 text-slate-400 border border-slate-200'}`}>
    {active ? 'Active' : 'Inactive'}
  </span>
);

// --- The Add/Edit drawer. Owns the whole Drawer call (title/children/footer)
// and is only ever mounted while open (see its call site in ShiftTypesPage --
// `{drawer.isOpen && <ShiftTypeDrawer .../>}`), so every open starts with
// fresh state: no reset effect needed, no stale state possible across opens.
const ShiftTypeDrawer = ({ mode, row, existingRows, onSubmit, onClose }) => {
  const [code, setCode] = useState(row?.code || '');
  const [id, setId] = useState(row?.id || '');
  const [idTouched, setIdTouched] = useState(false);
  const [label, setLabel] = useState(row?.label || '');
  const [start, setStart] = useState(row?.start || '08:00');
  const [end, setEnd] = useState(row?.end || '17:00');
  const [breakMinutes, setBreakMinutes] = useState(row?.breakMinutes ?? '0');
  const [color, setColor] = useState(row?.color || '#D1D5DB');
  const [isActive, setIsActive] = useState(row?.isActive ?? true);
  const [formError, setFormError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const handleCodeChange = (value) => {
    setCode(value);
    // Live auto-link uses the bare slug (possibly ''), never the 'shift'
    // fallback -- clearing Code to retype it should clear the ID preview,
    // not flash a fallback id that might collide with an unrelated row.
    if (mode === 'add' && !idTouched) setId(slugify(value));
  };

  const handleIdChange = (value) => {
    setIdTouched(true);
    setId(value);
  };

  const takenIds = useMemo(
    () => new Set(existingRows.filter((r) => r.id !== row?.id).map((r) => r.id)),
    [existingRows, row]
  );
  // Bare slug (no fallback) for the live "already in use" check -- an id
  // that hasn't sluggified into anything real yet is caught by validateForm
  // on submit, not flagged here as colliding with whatever 'shift' happens
  // to already exist.
  const idRawSlug = slugify(id);
  const idIsTaken = mode === 'add' && idRawSlug !== '' && takenIds.has(idRawSlug);
  const suggestion = idIsTaken ? disambiguateId(idRawSlug, takenIds) : null;

  const overnightPreview = deriveOvernight(start, end);

  const handleSubmit = async () => {
    const form = { code, id, label, start, end, breakMinutes, color, isActive };
    const err = validateForm(form, mode, existingRows, row);
    if (err) { setFormError(err); return; }
    setIsSaving(true);
    try {
      const result = await onSubmit(form);
      if (!result.ok) setFormError(result.message);
    } catch {
      // A rejected fetch (offline, dropped connection, backend restart)
      // never reaches onSubmit's own {ok:false} path -- without this catch
      // isSaving would never clear and the button spins forever.
      setFormError('Could not reach the server. Check your connection and try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // While saving, Cancel/X/backdrop closing the drawer would only hide the
  // request, not stop it -- onSubmit still lands and mutates rows afterward.
  // Block the close until the request settles instead.
  const guardedClose = () => { if (!isSaving) onClose(); };

  return (
    <Drawer
      isOpen
      onClose={guardedClose}
      title={mode === 'add' ? 'Add Shift Type' : 'Edit Shift Type'}
      footer={(
        <div className="flex gap-3">
          <Button variant="secondary" onClick={guardedClose} disabled={isSaving} className="flex-1">Cancel</Button>
          <Button onClick={handleSubmit} isLoading={isSaving} className="flex-1">
            {mode === 'add' ? 'Create' : 'Save Changes'}
          </Button>
        </div>
      )}
    >
      <div className="space-y-5">
        <div className="grid grid-cols-2 gap-4">
          <div className="flex flex-col gap-1.5">
            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.15em] ml-1">Code</label>
            <input
              type="text" value={code} onChange={(e) => handleCodeChange(e.target.value)}
              placeholder="e.g. MID"
              className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-bold text-slate-700 focus:ring-2 focus:ring-[var(--primary)]/20 focus:border-[var(--primary)] outline-none transition-all"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.15em] ml-1 flex items-center gap-1.5">
              Internal ID {mode === 'edit' && <Lock className="w-3 h-3" />}
            </label>
            {mode === 'add' ? (
              <input
                type="text" value={id} onChange={(e) => handleIdChange(e.target.value)}
                className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-mono font-bold text-slate-700 focus:ring-2 focus:ring-[var(--primary)]/20 focus:border-[var(--primary)] outline-none transition-all"
              />
            ) : (
              <div className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-mono font-bold text-slate-400">
                {row?.id}
              </div>
            )}
          </div>
        </div>
        {mode === 'add' ? (
          idIsTaken ? (
            <p className="text-[11px] font-semibold text-rose-600 -mt-3 ml-1">
              This ID is already in use.{' '}
              <button type="button" onClick={() => handleIdChange(suggestion)} className="underline decoration-dotted hover:text-rose-700">
                Use "{suggestion}" instead
              </button>
            </p>
          ) : (
            <p className="text-[11px] text-slate-400 -mt-3 ml-1 leading-relaxed">
              Used internally by scheduling rules (e.g. cross-coverage, fairness balancing) and cannot be changed after creation. Edit if you need a specific value.
            </p>
          )
        ) : (
          <p className="text-[11px] text-slate-400 -mt-3 ml-1">Fixed at creation — cannot be changed.</p>
        )}

        <div className="flex flex-col gap-1.5">
          <label className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.15em] ml-1">Label <span className="normal-case tracking-normal text-slate-300">(optional)</span></label>
          <input
            type="text" value={label} onChange={(e) => setLabel(e.target.value)}
            className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-semibold text-slate-700 focus:ring-2 focus:ring-[var(--primary)]/20 focus:border-[var(--primary)] outline-none transition-all"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="flex flex-col gap-1.5">
            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.15em] ml-1">Start</label>
            <input
              type="text" value={start} onChange={(e) => setStart(e.target.value)} placeholder="HH:MM"
              className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-semibold text-slate-700 focus:ring-2 focus:ring-[var(--primary)]/20 focus:border-[var(--primary)] outline-none transition-all"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.15em] ml-1">End</label>
            <input
              type="text" value={end} onChange={(e) => setEnd(e.target.value)} placeholder="HH:MM"
              className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-semibold text-slate-700 focus:ring-2 focus:ring-[var(--primary)]/20 focus:border-[var(--primary)] outline-none transition-all"
            />
          </div>
        </div>

        <div className="flex items-center justify-between px-1">
          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.15em]">Overnight (computed)</span>
          <OvernightBadge value={overnightPreview} />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="flex flex-col gap-1.5">
            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.15em] ml-1">Break (minutes)</label>
            <input
              type="number" min="0" step="1" value={breakMinutes} onChange={(e) => setBreakMinutes(e.target.value)}
              className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-semibold text-slate-700 focus:ring-2 focus:ring-[var(--primary)]/20 focus:border-[var(--primary)] outline-none transition-all"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.15em] ml-1">Color</label>
            <div className="flex items-center gap-2">
              <span className="w-9 h-9 rounded-lg border border-slate-200 flex-shrink-0" style={{ background: color || '#D1D5DB' }} />
              <input
                type="text" value={color} onChange={(e) => setColor(e.target.value)} placeholder="#D1D5DB"
                className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-semibold text-slate-700 focus:ring-2 focus:ring-[var(--primary)]/20 focus:border-[var(--primary)] outline-none transition-all"
              />
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between px-1">
          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.15em]">Active</span>
          <Switch isOn={isActive} onClick={() => setIsActive(!isActive)} activeTrack="bg-emerald-500" />
        </div>

        {formError && (
          <p className="text-[11px] font-semibold text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">
            {formError}
          </p>
        )}
      </div>
    </Drawer>
  );
};

const ShiftTypesPage = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [banner, setBanner] = useState({ type: null, message: '' });
  const [drawer, setDrawer] = useState({ isOpen: false, mode: 'add', row: null });
  const [confirmDelete, setConfirmDelete] = useState({ isOpen: false, row: null });
  const [isDeleting, setIsDeleting] = useState(false);

  const activeCount = useMemo(() => rows.filter((row) => row.isActive).length, [rows]);

  const fetchShiftTypes = useCallback(async () => {
    setLoading(true);
    setBanner({ type: null, message: '' });
    try {
      const response = await apiFetch('/api/shift-types');
      const body = await parseJsonSafe(response);
      if (!response.ok || !Array.isArray(body)) {
        setRows([]);
        setBanner({ type: 'error', message: 'Failed to load shift types.' });
        return;
      }
      setRows(body.map(normalizeRow));
    } catch {
      setRows([]);
      setBanner({ type: 'error', message: 'Failed to load shift types.' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchShiftTypes();
  }, [fetchShiftTypes]);

  // --- Create: patches local state from the payload just sent, merged with
  // the server's authoritative is_overnight (POST returns only {success,
  // is_overnight} -- no full object). Never refetches. ---
  const handleCreate = async (form) => {
    const newId = normalizeIdFromCode(form.id);
    const payload = { id: newId, ...toApiPayload(form) };
    const response = await apiFetch('/api/shift-types', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const body = await parseJsonSafe(response);
    if (!isApiSuccess(response, body)) {
      return { ok: false, message: body?.message || 'Failed to save shift type.' };
    }
    const newRow = normalizeRow({
      id: newId, short_code: payload.short_code, label: payload.label,
      start_time: payload.start_time, end_time: payload.end_time,
      break_minutes: payload.break_minutes, color: payload.color,
      is_active: payload.is_active, is_overnight: body.is_overnight,
    });
    setRows((prev) => [...prev, newRow]);
    setBanner({ type: 'success', message: 'Shift type created.' });
    setDrawer({ isOpen: false, mode: 'add', row: null });
    return { ok: true };
  };

  // --- Edit: patches ONLY the matching row. Never refetches, so no other
  // row's in-progress edit can be clobbered -- the bug this redesign fixes. ---
  const handleEdit = async (originalRow, form) => {
    const payload = toApiPayload(form);
    const response = await apiFetch(`/api/shift-types/${encodeURIComponent(originalRow.id)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const body = await parseJsonSafe(response);
    if (!isApiSuccess(response, body)) {
      return { ok: false, message: body?.message || 'Failed to save shift type.' };
    }
    const updatedRow = normalizeRow({
      id: originalRow.id, short_code: payload.short_code, label: payload.label,
      start_time: payload.start_time, end_time: payload.end_time,
      break_minutes: payload.break_minutes, color: payload.color,
      is_active: payload.is_active, is_overnight: body.is_overnight,
    });
    setRows((prev) => prev.map((r) => (r.id === originalRow.id ? updatedRow : r)));
    setBanner({ type: 'success', message: 'Shift type updated.' });
    setDrawer({ isOpen: false, mode: 'edit', row: null });
    return { ok: true };
  };

  const handleDeleteConfirm = async () => {
    if (isDeleting) return;
    const row = confirmDelete.row;
    setIsDeleting(true);
    try {
      const response = await apiFetch(`/api/shift-types/${encodeURIComponent(row.id)}`, { method: 'DELETE' });
      const body = await parseJsonSafe(response);

      if (isApiSuccess(response, body)) {
        setRows((prev) => prev.filter((r) => r.id !== row.id));
        setBanner({ type: 'success', message: 'Shift type deleted.' });
        setConfirmDelete({ isOpen: false, row: null });
        return;
      }

      // Blocked because it's still referenced somewhere -- deactivate instead.
      const deactivateResponse = await apiFetch(`/api/shift-types/${encodeURIComponent(row.id)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...toApiPayload(row), is_active: false }),
      });
      const deactivateBody = await parseJsonSafe(deactivateResponse);
      if (isApiSuccess(deactivateResponse, deactivateBody)) {
        setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, isActive: false } : r)));
        setBanner({ type: 'success', message: 'Shift type is in use; set to inactive instead.' });
        setConfirmDelete({ isOpen: false, row: null });
        return;
      }

      setConfirmDelete({ isOpen: false, row: null });
      setBanner({ type: 'error', message: body?.message || 'Failed to delete shift type.' });
    } catch {
      // A rejected fetch (offline, dropped connection) never reaches the
      // isApiSuccess checks above -- without this the failure was silent.
      setConfirmDelete({ isOpen: false, row: null });
      setBanner({ type: 'error', message: 'Could not reach the server. Check your connection and try again.' });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="animate-in fade-in space-y-6">
      <PageHeader icon={Clock3} title="Shift Type Manager" />


      {banner.type && (
        <div
          className={`flex items-center justify-between gap-2 px-4 py-2.5 rounded-xl border text-sm font-semibold ${
            banner.type === 'success'
              ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
              : 'bg-rose-50 border-rose-200 text-rose-700'
          }`}
        >
          <div className="flex items-center gap-2">
            {banner.type === 'success' ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
            <span>{banner.message}</span>
          </div>
          {banner.type === 'error' && (
            <Button variant="ghost" size="sm" onClick={fetchShiftTypes}>Retry</Button>
          )}
        </div>
      )}

      {/* One card per shift type (Oct 2026: every list is cards). */}
      <div className="flex items-baseline gap-3 ml-1">
        <span className="text-[11px] font-black text-slate-400 uppercase tracking-[0.2em]">Shift types</span>
        <span className="text-[10px] font-bold text-slate-300">{activeCount} active / {rows.length} total</span>
      </div>
      {loading && <p className="text-xs text-slate-400 font-semibold ml-1">Loading shift types...</p>}
      {!loading && rows.length === 0 && <p className="text-xs text-slate-400 font-semibold ml-1">No shift types yet.</p>}
      <div className={CARD_GRID}>
        {!loading && rows.map((row) => {
          const overnight = row.isOvernight ?? deriveOvernight(row.start, row.end);
          return (
            <ItemCard key={row.id} title={row.code} accent={row.color}
              badges={<><StatusBadge active={row.isActive} /><OvernightBadge value={overnight} /></>}
              onEdit={() => setDrawer({ isOpen: true, mode: 'edit', row })}
              onDelete={() => setConfirmDelete({ isOpen: true, row })}
              footerLeft={row.label || 'No label'}
              footerRight={<span className="font-mono normal-case tracking-normal">{row.color}</span>}>
              <CardSection icon={Clock3} label="Hours">
                <p className="text-sm font-bold text-slate-700 ml-1">
                  {row.start}–{row.end}
                  <span className="ml-2 text-xs font-semibold text-slate-400">{row.breakMinutes}m break</span>
                </p>
              </CardSection>
            </ItemCard>
          );
        })}
      </div>

      {/* Adding, at the bottom of the list like everywhere else (Oct 2026). */}
      <AddNewButton label="New shift type" onClick={() => setDrawer({ isOpen: true, mode: 'add', row: null })} />

      {drawer.isOpen && (
        <ShiftTypeDrawer
          mode={drawer.mode}
          row={drawer.row}
          existingRows={rows}
          onClose={() => setDrawer({ isOpen: false, mode: drawer.mode, row: null })}
          onSubmit={(form) => (drawer.mode === 'add' ? handleCreate(form) : handleEdit(drawer.row, form))}
        />
      )}

      <ConfirmDialog
        isOpen={confirmDelete.isOpen}
        onClose={() => setConfirmDelete({ isOpen: false, row: null })}
        title={`Delete "${confirmDelete.row?.code}"?`}
        body="This will permanently remove the shift type. If it's still referenced by a location's shift pattern, coverage requirement, or staffing requirement, it will be deactivated instead of deleted."
        tone="danger"
        confirmLabel="Delete"
        onConfirm={handleDeleteConfirm}
        cancelLabel="Cancel"
        isLoading={isDeleting}
      />
    </div>
  );
};

export default ShiftTypesPage;
